import * as turf from "@turf/turf";
import JSZip from "jszip";
import tokml from "tokml";
import * as XLSX from "xlsx";
import {
  Feature,
  FeatureCollection,
  Geometry,
  MultiPolygon,
  Polygon,
} from "geojson";
import { geoServer, geoServerDefaultParams } from "../services/geoServer";
import { Layer } from "../global/constants/layers";
import { buildCqlFilter } from "./layerCqlFilter";
import { ApiGetProjectionAreaStats } from "../services/api";
import { getGeometryField } from "./geometryField";

// A exportação só é permitida para áreas pequenas — acima disso o volume de
// feições/requisições ao GeoServer torna o download inviável.
export const AREA_EXPORT_MAX_AREA_HA = 10;
export const AREA_EXPORT_MAX_AREA_M2 = AREA_EXPORT_MAX_AREA_HA * 10_000;

export function isAreaExportable(areaM2: number): boolean {
  return areaM2 > 0 && areaM2 <= AREA_EXPORT_MAX_AREA_M2;
}

// tokml only merges its name/description/timestamp defaults when NO options
// object is passed at all (`options || {defaults}`) — passing documentName
// alone would silently drop every placemark's <name> tag.
const TOKML_DEFAULTS = {
  name: "name",
  description: "description",
  timestamp: "timestamp",
} as const;

function toKml(fc: FeatureCollection, documentName: string): string {
  return tokml(fc, { ...TOKML_DEFAULTS, documentName });
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Renders every raw property as an HTML table so it shows in the placemark's
 * description balloon — not every KML viewer surfaces ExtendedData prominently,
 * but description is rendered everywhere. */
function buildDescriptionHtml(properties: Record<string, unknown>): string {
  const rows = Object.entries(properties)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    )
    .map(
      ([key, value]) =>
        `<tr><td><b>${escapeHtml(key)}</b></td><td>${escapeHtml(
          value
        )}</td></tr>`
    )
    .join("");
  return rows ? `<table>${rows}</table>` : "";
}

// Common Portuguese/English "display name" fields across GeoServer layers —
// first match wins, so a real attribute is used instead of a blank placemark.
const NAME_CANDIDATE_KEYS = [
  "name",
  "nome",
  "nm_mun",
  "nm_uf",
  "titulo",
  "descricao",
];

function pickFeatureName(
  properties: Record<string, unknown>,
  fallback: string
): string {
  for (const key of NAME_CANDIDATE_KEYS) {
    const value = properties[key];
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return String(value);
    }
  }
  return fallback;
}

/** Attaches a display name + an HTML attribute table to a feature so tokml
 * renders both a <name> and a populated <description> for every placemark. */
function withDisplayProperties(
  feature: Feature,
  fallbackName: string
): Feature {
  const properties = feature.properties ?? {};
  return {
    ...feature,
    properties: {
      ...properties,
      name: pickFeatureName(properties, fallbackName),
      description: buildDescriptionHtml(properties),
    },
  };
}

// Excel rejects sheet names over 31 chars, containing \ / ? * [ ] : or
// duplicated (case-insensitively) within the workbook.
const SHEET_NAME_MAX = 31;

function toSheetName(title: string, usedLower: Set<string>): string {
  const base =
    title
      .replace(/[\\/?*[\]:]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, SHEET_NAME_MAX) || "Camada";
  let name = base;
  let counter = 2;
  while (usedLower.has(name.toLowerCase())) {
    const suffix = ` (${counter})`;
    name = base.slice(0, SHEET_NAME_MAX - suffix.length) + suffix;
    counter += 1;
  }
  usedLower.add(name.toLowerCase());
  return name;
}

function toCellValue(value: unknown): string | number | boolean | null {
  if (value === null || value === undefined) return null;
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "string"
  ) {
    return value;
  }
  return JSON.stringify(value);
}

interface AttributeSheet {
  title: string;
  rows: Record<string, string | number | boolean | null>[];
}

function featuresToAttributeRows(features: Feature[]): AttributeSheet["rows"] {
  return features.map((feature) =>
    Object.fromEntries(
      Object.entries(feature.properties ?? {}).map(([key, value]) => [
        key,
        toCellValue(value),
      ])
    )
  );
}

function buildAttributesWorkbook(sheets: AttributeSheet[]): ArrayBuffer | null {
  const nonEmpty = sheets.filter((sheet) => sheet.rows.length > 0);
  if (nonEmpty.length === 0) return null;

  const workbook = XLSX.utils.book_new();
  const usedLower = new Set<string>();
  for (const sheet of nonEmpty) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(sheet.rows),
      toSheetName(sheet.title, usedLower)
    );
  }
  return XLSX.write(workbook, { type: "array", bookType: "xlsx" });
}

function slugify(text: string): string {
  const slug = text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return slug || "camada";
}

function selectionBbox(
  selection: Polygon | MultiPolygon
): [number, number, number, number] {
  return turf.bbox(turf.feature(selection)) as [number, number, number, number];
}

/**
 * BBOX of the selection, padded so features that only graze the edge still come
 * back — the exact clipping is done client-side by `clipFeatureCollection`.
 */
function buildBboxPredicate(
  geometryField: string,
  selection: Polygon | MultiPolygon
): string {
  const [minX, minY, maxX, maxY] = selectionBbox(selection);
  return `BBOX(${geometryField}, ${minX}, ${minY}, ${maxX}, ${maxY}, 'EPSG:4326')`;
}

/**
 * Always bounded to the selection's BBOX — never the whole layer. Without an
 * attribute filter the plain `bbox` param does it; with one, the BBOX has to go
 * inside the CQL (the two params are mutually exclusive), which needs the
 * geometry column. If that can't be discovered this rejects instead of
 * fetching unbounded, and the caller reports the layer as failed.
 */
export async function fetchLayerFeatures(
  layer: Layer,
  cqlFilter: string | undefined,
  selection: Polygon | MultiPolygon
): Promise<FeatureCollection> {
  const typeName = `${layer.workspace}:${layer.layerName}`;
  const params: Record<string, unknown> = {
    ...geoServerDefaultParams,
    typename: typeName,
    srsName: "EPSG:4326",
  };

  if (cqlFilter) {
    const geometryField = await getGeometryField(typeName);
    params.cql_filter = `${buildBboxPredicate(geometryField, selection)} AND (${cqlFilter})`;
  } else {
    params.bbox = selectionBbox(selection).join(",");
  }

  const response = await geoServer.get<FeatureCollection>("wfs", { params });
  return response.data;
}

function isPolygonal(geometry: Geometry): geometry is Polygon | MultiPolygon {
  return geometry.type === "Polygon" || geometry.type === "MultiPolygon";
}

/**
 * Clips each feature to the selection polygon. Polygons are truly clipped via
 * turf.intersect; points/lines are kept whole and only filtered by
 * intersection — turf has no partial line-clip without pulling in another
 * dependency, so a line that dips into the area is exported in full.
 */
function clipFeatureCollection(
  fc: FeatureCollection,
  selection: Polygon | MultiPolygon
): FeatureCollection {
  const selectionFeature = turf.feature(selection);
  const clipped: Feature[] = [];

  for (const feature of fc.features) {
    if (!feature.geometry) continue;
    try {
      if (isPolygonal(feature.geometry)) {
        const result = turf.intersect(
          turf.featureCollection([
            turf.feature(feature.geometry),
            selectionFeature,
          ]) as FeatureCollection<Polygon | MultiPolygon>,
          { properties: feature.properties ?? {} }
        );
        if (result) clipped.push(result);
      } else if (feature.geometry.type === "Point") {
        if (turf.booleanPointInPolygon(feature.geometry, selectionFeature)) {
          clipped.push(feature);
        }
      } else if (turf.booleanIntersects(feature.geometry, selection)) {
        clipped.push(feature);
      }
    } catch {
      // A single malformed/self-intersecting feature shouldn't abort the whole layer
    }
  }

  return { type: "FeatureCollection", features: clipped };
}

export interface ExportProjectionOption {
  label: string;
  layerName: string;
}

export interface ExportSelectedAreaParams {
  geometry: Polygon | MultiPolygon;
  areaName: string;
  activeLayers: Layer[];
  layerCqlFilters: Record<string, (number | string)[]>;
  projectionOptions: readonly ExportProjectionOption[];
  hubHeightM?: number;
}

export interface ExportSelectedAreaResult {
  succeeded: string[];
  failed: string[];
}

export async function exportSelectedArea({
  geometry,
  areaName,
  activeLayers,
  layerCqlFilters,
  projectionOptions,
  hubHeightM,
}: ExportSelectedAreaParams): Promise<ExportSelectedAreaResult> {
  const zip = new JSZip();
  const succeeded: string[] = [];
  const failed: string[] = [];

  const selectionFc: FeatureCollection = {
    type: "FeatureCollection",
    features: [
      withDisplayProperties(
        { type: "Feature", properties: { name: areaName }, geometry },
        areaName
      ),
    ],
  };
  zip.file("area-selecionada.kml", toKml(selectionFc, areaName));

  // Slotted by layer index so the concurrent fetches don't shuffle the order
  // of the Excel tabs relative to the layer list.
  const attributeSheets: (AttributeSheet | undefined)[] = new Array(
    activeLayers.length
  );

  await Promise.allSettled(
    activeLayers.map(async (layer, layerIndex) => {
      try {
        const voltageCql = buildCqlFilter(
          layer.voltageOptions,
          layerCqlFilters[layer.layerName] ?? []
        );
        const cqlFilter =
          [layer.cqlFilter, voltageCql].filter(Boolean).join(" AND ") ||
          undefined;
        const raw = await fetchLayerFeatures(layer, cqlFilter, geometry);
        const clipped = clipFeatureCollection(raw, geometry);
        if (clipped.features.length === 0) {
          failed.push(`${layer.title} (sem feições na área selecionada)`);
          return;
        }
        attributeSheets[layerIndex] = {
          title: layer.title,
          rows: featuresToAttributeRows(clipped.features),
        };
        const withAttributes: FeatureCollection = {
          type: "FeatureCollection",
          features: clipped.features.map((feature, index) =>
            withDisplayProperties(feature, `${layer.title} ${index + 1}`)
          ),
        };
        zip.file(
          `camadas/${slugify(layer.title)}.kml`,
          toKml(withAttributes, layer.title)
        );
        succeeded.push(layer.title);
      } catch (error) {
        console.error(
          `[areaExport] Falha ao exportar camada ${layer.title}:`,
          error
        );
        failed.push(layer.title);
      }
    })
  );

  await Promise.allSettled(
    projectionOptions.map(async ({ label, layerName }) => {
      try {
        const stats = await ApiGetProjectionAreaStats({
          layer_name: layerName,
          geometry,
          hub_height_m: hubHeightM,
        });
        if (!stats.propitious_geometry) return;

        const fc: FeatureCollection = {
          type: "FeatureCollection",
          features: [
            withDisplayProperties(
              {
                type: "Feature",
                properties: {
                  name: label,
                  area_propicia_km2: stats.propitious_area_km2,
                  percentual_da_area_total: stats.percentage,
                  potencial_instalavel_gw: stats.installable_gw,
                  energia_anual_gwh: stats.annual_energy_gwh,
                },
                geometry: stats.propitious_geometry,
              },
              label
            ),
          ],
        };
        zip.file(
          `areas-propicias/${slugify(label)}.kml`,
          toKml(fc, `Área Propícia · ${label}`)
        );
        succeeded.push(`Área Propícia · ${label}`);
      } catch (error) {
        console.error(
          `[areaExport] Falha ao exportar projeção ${label}:`,
          error
        );
        failed.push(`Área Propícia · ${label}`);
      }
    })
  );

  try {
    const workbook = buildAttributesWorkbook(
      attributeSheets.filter((sheet): sheet is AttributeSheet => Boolean(sheet))
    );
    if (workbook) zip.file("tabela-de-atributos.xlsx", workbook);
  } catch (error) {
    console.error("[areaExport] Falha ao gerar tabela de atributos:", error);
    failed.push("Tabela de atributos (Excel)");
  }

  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${slugify(areaName)}-exportacao-${
    new Date().toISOString().split("T")[0]
  }.zip`;
  link.click();
  URL.revokeObjectURL(url);

  return { succeeded, failed };
}
